<script lang="ts">
export default {
  name: 'Notifications',
};
</script>

<script setup lang="ts">
import { MenuSquare, Bell, X } from 'lucide-vue-next';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { IsUserAnAdmin, UserHasPermission } from '@/utilities/AuthUtils';
import Store, { AppState, Commit } from '@/store';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import notificationService from '@/services/v2/NotificationService';
import { relativeDateTime } from '@/utilities/Dates';
import { StoreMutationType } from '@/types/StoreMutationType';
import axios from 'axios';
import { UserPermission } from '@/types/UserPermissions';
import { compactNumber } from '@/utilities/Number.js';
import { sanitizeHtml } from '@/utilities/Sanitization';

const MAX_VIEWABLE_NOTIFICATIONS = 5;

// ---------
// refs
// ---------

const store = useStore<AppState>();
const router = useRouter();
const intervalId = ref(0);
const notificationRef = ref<HTMLElement | null>(null);
const notificationListRef = ref<HTMLElement | null>(null);
const isMenuOpen = ref(false);
const notificationsModifiedDate = ref<Date>(new Date());

// ---------
// computed
// ---------

const notifications = computed(() => store.state.notifications.slice(0, MAX_VIEWABLE_NOTIFICATIONS));
const totalNotificationCount = computed(() => store.state.notifications.length);
const readNotificationCount = computed(() => compactNumber(store.state.notifications.filter((n) => !n.isRead).length));
const hasNotifications = computed(() => store.state.notifications.filter((n) => !n.isRead).length > 0);
const canViewNotifications = computed(
  () => IsUserAnAdmin(store.state.user) || UserHasPermission(Store.state.user, UserPermission.NotificationsRead),
);

// ---------
// lifecycle
// ---------

onMounted(async () => {
  if (canViewNotifications.value) {
    Commit(StoreMutationType.SET_NOTIFICATIONS, await notificationService.getNotifications());
    notificationsModifiedDate.value = new Date();
    await notificationPoll();
  }
});

onUnmounted(() => {
  if (intervalId.value) {
    clearInterval(intervalId.value);
  }
});

async function notificationPoll() {
  intervalId.value = window.setInterval(async function () {
    try {
      const notifications = await notificationService.getNotifications(notificationsModifiedDate.value);
      notificationsModifiedDate.value = new Date();

      if (notifications.sort().join() !== store.state.notifications.sort().join()) {
        Commit(StoreMutationType.SET_NOTIFICATIONS, notifications);
      }
    } catch (e) {
      if (axios.isAxiosError(e) && e.response?.status === 304) {
        console.log('No new notifications');
      } else if (axios.isAxiosError(e) && e.response?.status && e.response.status >= 400 && e.response.status <= 599) {
        // Stop polling on client errors (401, 403, 404, etc.) and server errors (500, 502, 503, etc.)
        console.error('Stopping notification polling due to error:', e.response.status);
        if (intervalId.value) {
          window.clearInterval(intervalId.value);
          intervalId.value = 0;
        }
      } else {
        console.error(e);
      }
    }
  }, 600_000);
}

// ---------
// watchers
// ---------
watch(isMenuOpen, (isOpen) => {
  if (!isOpen) {
    blurActiveElement();
  }
});

// ----------
// functions
// ----------

function blurActiveElement() {
  const activeElement = document.activeElement as HTMLElement;
  activeElement.blur();
}

function toggleMenuClick() {
  isMenuOpen.value = !isMenuOpen.value;
}

async function handleNotificationDelete(id: number) {
  try {
    await notificationService.deleteNotification(id);
    Commit(
      StoreMutationType.SET_NOTIFICATIONS,
      store.state.notifications.filter((n) => n.id !== id),
    );
  } catch (e) {
    console.error(e);
  }
}

async function handleNotificationClick(id: number) {
  const notification = notifications.value.find((n) => n.id === id);
  if (!notification) {
    return;
  }

  if (!notification.isRead) {
    try {
      await notificationService.markAsRead(id);
      Commit(
        StoreMutationType.SET_NOTIFICATIONS,
        store.state.notifications.map((n) => {
          if (n.id === id) {
            n.isRead = true;
          }
          return n;
        }),
      );
    } catch (e) {
      console.error(e);
    }
  }

  notificationRef.value?.blur();
  notificationListRef.value?.blur();

  switch (notification.type) {
    case 'new-policies-added':
      if (notification.payload && notification.payload.accountId) {
        // Note:
        // doing this to allow page render for urls that match the same pattern as the current page.
        // the vue router will not re-render the page if the url pattern matches the current page regardless if the account id is different.
        // The :key was removed from the main <router-view> so that it no longer re-renders the whole page which was causing the page header
        // to bounce around.
        await router.push('/');
        await router.replace({
          path: `/account/${notification.payload.accountId}/policy-detail`,
          force: true,
        });
      }
      break;
    case 'policies-up-for-renewal':
      if (notification.payload && notification.payload.accountId && notification.payload.programId) {
        await router.push('/');
        await router.replace({
          path: `/account/${notification.payload.accountId}/programs/${notification.payload.programId}`,
          force: true,
        });
      }
      break;
    case 'new-document-upload':
      if (notification.payload && notification.payload.accountId) {
        await router.push('/');
        await router.replace({
          path: `/account/${notification.payload.accountId}/documents`,
          force: true,
        });
      }
      break;
    default:
      console.error('No action for notification type', notification.type);
      break;
  }
}

function handleViewAllClick() {
  notificationRef.value?.blur();
  notificationListRef.value?.blur();
  router.push('/notifications');
}

function sanitizeNotificationMessage(message: string): string {
  return sanitizeHtml(message);
}
</script>

<template>
  <div
    class="tw:dropdown tw:dropdown-end tw:dropdown-bottom tw:ml-2 tw:flex tw:h-full tw:items-center tw:transition-all">
    <div
      ref="notificationRef"
      tabindex="0"
      role="button"
      class="tw:group tw:btn tw:relative tw:border-none tw:btn-ghost tw:btn-lg tw:hover:border-none tw:hover:bg-gray-500/10 tw:hover:shadow-none"
      :class="{ 'tw:mr-6' : hasNotifications }"
      @blur="isMenuOpen = false"
      @click="toggleMenuClick">
      <Bell :size="15" class="tw:absolute tw:stroke-white tw:group-hover:rotate-12" />
      <div
        v-visible="hasNotifications"
        class="tw:absolute tw:top-1 tw:left-6 tw:badge tw:border-0 tw:bg-agency-red tw:badge-sm tw:text-white">
        {{ readNotificationCount }}
      </div>
    </div>
    <ul
      v-visible="totalNotificationCount > 0"
      tabindex="0"
      class="tw:dropdown-content tw:menu tw:w-80 tw:rounded-lg tw:bg-white tw:p-0 tw:px-1 tw:shadow-md tw:shadow-gray-500"
      ref="notificationListRef"
      @blur="isMenuOpen = false">
      <li
        v-for="notification in notifications"
        class="tw:group tw:border-x-0 tw:border-t-1 tw:border-b-0 tw:border-solid tw:border-gray-300 tw:first:border-t-0"
        :key="notification.id"
        ref="notificationItemRefs">
        <a
          class="tw:relative tw:flex tw:rounded-none tw:px-0"
          style="text-wrap: inherit"
          @click="handleNotificationClick(notification.id)">
          <div class="tw:flex tw:w-full tw:flex-col tw:items-start tw:gap-0 tw:px-2">
            <div class="tw:flex tw:items-center tw:justify-stretch tw:gap-2">
              <div v-if="!notification.isRead" class="tw:badge tw:bg-green-400 tw:badge-xs"></div>
              <div class="tw:text-agency-red" :class="{ 'tw:text-agency-red/70': notification.isRead }">
                <p class="tw:line-clamp-2" v-html="sanitizeNotificationMessage(notification.message)"></p>
              </div>
            </div>
            <div class="tw:scale-90 tw:self-end tw:text-xs tw:text-gray-600">
              {{ relativeDateTime(notification.createdAt) }}
            </div>
          </div>
          <div class="tw:absolute tw:flex tw:h-full tw:w-full tw:justify-end">
            <X
              @click.stop="handleNotificationDelete(notification.id)"
              :size="16"
              class="tw:h-full tw:stroke-black tw:opacity-60 tw:hover:stroke-agency-red tw:hover:opacity-100" />
          </div>
        </a>
      </li>
      <li class="tw:border-x-0 tw:border-t-1 tw:border-b-0 tw:border-solid tw:border-gray-300">
        <a
          @click="handleViewAllClick"
          class="tw:flex tw:items-center tw:justify-center tw:rounded-none tw:px-2 tw:py-1">
          <div class="tw:flex tw:items-center tw:gap-2 tw:p-1">
            <div class="tw:text-agency-red">View All</div>
            <MenuSquare :size="18" class="tw:text-agency-red" />
          </div>
        </a>
      </li>
    </ul>
    <div
      v-visible="totalNotificationCount === 0"
      tabindex="0"
      class="tw:dropdown-content tw:menu tw:h-80 tw:w-80 tw:rounded-lg tw:bg-base-100 tw:p-2 tw:text-black tw:shadow-md tw:shadow-gray-500"
      @blur="isMenuOpen = false">
      <div class="tw:m-auto tw:flex tw:flex-col tw:items-center tw:justify-center">
        <span>No new notifications!</span>
        <router-link class="tw:p-1" to="/notifications/preferences">
          <span class="tw:font-bold tw:underline">View Settings</span>
        </router-link>
      </div>
    </div>
  </div>
</template>
